import type { ReactNode } from 'react';

import { PortalFadeIn } from '@/modules/portal/components/ui/portal-motion';
import { cn } from '@/shared/lib/cn';

export interface PortalTimelineItem {
  id: string;
  title: string;
  subtitle?: string;
  status?: 'complete' | 'current' | 'upcoming';
  meta?: ReactNode;
}

interface PortalTimelineProps {
  title: string;
  items: PortalTimelineItem[];
  className?: string;
}

const dotStyles = {
  complete: 'bg-primary ring-4 ring-primary/15',
  current: 'bg-primary ring-4 ring-primary/25 scale-110',
  upcoming: 'bg-muted-foreground/30 ring-4 ring-muted/40',
} as const;

export function PortalTimeline({ title, items, className }: PortalTimelineProps) {
  return (
    <PortalFadeIn delay={0.08}>
      <section className={cn('portal-glass-panel rounded-2xl p-5 sm:p-6', className)}>
        <h2 className="text-base font-semibold text-foreground">{title}</h2>
        <ol className="relative mt-5 space-y-0">
          {items.map((item, index) => {
            const status = item.status ?? 'upcoming';
            const isLast = index === items.length - 1;

            return (
              <li key={item.id} className="relative flex gap-4 pb-6 last:pb-0">
                {!isLast ? (
                  <span
                    className="absolute start-[0.4375rem] top-5 h-[calc(100%-0.5rem)] w-px bg-border"
                    aria-hidden
                  />
                ) : null}
                <span
                  className={cn(
                    'relative z-10 mt-1.5 h-3.5 w-3.5 shrink-0 rounded-full transition-transform',
                    dotStyles[status],
                  )}
                  aria-hidden
                />
                <div className="min-w-0 flex-1 pt-0.5">
                  <div className="flex flex-wrap items-baseline justify-between gap-2">
                    <p
                      className={cn(
                        'text-sm font-medium',
                        status === 'upcoming' ? 'text-muted-foreground' : 'text-foreground',
                      )}
                    >
                      {item.title}
                    </p>
                    {item.meta}
                  </div>
                  {item.subtitle ? (
                    <p className="mt-0.5 text-sm text-muted-foreground">{item.subtitle}</p>
                  ) : null}
                </div>
              </li>
            );
          })}
        </ol>
      </section>
    </PortalFadeIn>
  );
}
